import * as React from "react";
import { useState, useEffect } from "react";
import { ExtraPageInfo, ExtraPageResource } from "burde";
import { ExtraPagePageSize } from "svg/constants";
import * as styles from "./selected-extra-pages.css";
import Select from "react-select";
import Button from "content/button/button";
import { useResources } from "contexts/resource-context";

type Props = {
    selectedExtraPages: number[];
    extraPageResources: ExtraPageResource[] | undefined;
    onUpdateExtraPages: (extraPages: number[]) => any;
    onUpdatePreviewedExtraPage: (id: number) => void;
    numberOfPagesLeft: number;
};

type TranscribedExtraPageList = ExtraPageInfo & {
    quantity: number;
};

const SelectedExtraPages = ({
    selectedExtraPages,
    extraPageResources,
    onUpdateExtraPages,
    numberOfPagesLeft,
    onUpdatePreviewedExtraPage,
}: Props) => {
    const [transcribedExtraPages, setTranscribedExtraPages] = useState<
        TranscribedExtraPageList[]
    >([]);

    const resources = useResources();

    useEffect(() => {
        if (selectedExtraPages.length > 0) {
            setTranscribedExtraPages(
                getTranscribedListFromExtraPages(selectedExtraPages),
            );
        } else {
            setTranscribedExtraPages([]);
        }
    }, [selectedExtraPages]);

    const getExtraPageById = (extraPageId: number) => {
        if (extraPageResources !== undefined) {
            for (let i = 0; i < extraPageResources.length; i++) {
                for (let a = 0; a < extraPageResources[i].content.length; a++) {
                    if (extraPageResources[i].content[a].id === extraPageId) {
                        return extraPageResources[i].content[a];
                    }
                }
            }
        }

        return null;
    };

    const removeExtraPageByIndex = (index: number) => {
        const extraPages = [...transcribedExtraPages];
        extraPages.splice(index, 1);
        const updatedExtraPages = getCalendarExtraPagesFromTranscribedList(
            extraPages,
        );
        onUpdateExtraPages(updatedExtraPages);
    };

    const getCalendarExtraPagesFromTranscribedList = (
        transcribedList: TranscribedExtraPageList[],
    ) => {
        const extraPages = [];

        for (let i = 0; i < transcribedList.length; i++) {
            for (let a = 0; a < transcribedList[i].quantity; a += 2) {
                extraPages.push(transcribedList[i].extraPageId);
            }
        }

        return extraPages;
    };

    const getTranscribedListFromExtraPages = (extraPages: number[]) => {
        const transcribedList = [];
        let lastIterationId = null;
        let order = 1;
        for (let i = 0; i < selectedExtraPages.length; i++) {
            if (
                lastIterationId === null ||
                selectedExtraPages[i] !== lastIterationId
            ) {
                transcribedList.push({
                    extraPageId: selectedExtraPages[i],
                    quantity: ExtraPagePageSize,
                    order: order,
                });
                order++;
            } else if (selectedExtraPages[i] === lastIterationId) {
                transcribedList[
                    transcribedList.length - 1
                ].quantity += ExtraPagePageSize;
            }

            lastIterationId = selectedExtraPages[i];
        }

        return transcribedList;
    };

    const setExtraPageQuantityByIndex = (
        itemIndex: number,
        quantity: number,
    ) => {
        const updatedQuantityList = [...transcribedExtraPages];

        if (updatedQuantityList[itemIndex] !== undefined) {
            updatedQuantityList[itemIndex].quantity = quantity;

            const updatedList = getCalendarExtraPagesFromTranscribedList(
                updatedQuantityList,
            );
            onUpdateExtraPages(updatedList);
        }
    };

    const customStyles = {
        control: (provided: any, state: any) => ({
            ...provided,
            minHeight: "initial",
            height: "32px",
        }),
        valueContainer: (provided: any, state: any) => ({
            ...provided,
            padding: "0px 5px",
        }),
        dropdownIndicator: (provided: any, state: any) => ({
            ...provided,
            padding: 0,
        }),
        menu: (provided: any, state: any) => ({
            ...provided,
            top: "calc(100% - 3px)",
            bottom: "initial",
        }),
    };

    return (
        <ul className={styles.iconPicker}>
            {transcribedExtraPages.map((item, index) => {
                const numberOfPagesOptions = [];

                for (
                    let i = 2;
                    i <= numberOfPagesLeft + item.quantity;
                    i += 2
                ) {
                    numberOfPagesOptions.push({
                        label: i,
                        value: i,
                    });
                }

                const selectedQuantity = numberOfPagesOptions.find(
                    (o) => o.value === item.quantity,
                );

                const extraPage = getExtraPageById(item.extraPageId);
                if (extraPage !== null) {
                    return (
                        <React.Fragment key={index}>
                            <li
                                className={styles.iconPickerLi}
                                onClick={() => {}}>
                                <div
                                    className={[
                                        styles.iconPickerItem,
                                        styles.iconPickerItemSmall,
                                        styles.iconPickerItemImageAuto,
                                    ].join(" ")}>
                                    <div className={styles.extraPage}>
                                        <div
                                            className={
                                                styles.thumbnailAndPreview
                                            }
                                            onClick={() => {
                                                onUpdatePreviewedExtraPage(
                                                    extraPage.id,
                                                );
                                            }}>
                                            <div
                                                className={
                                                    styles.previewIconContainer
                                                }>
                                                <img src="/images/magnify.svg" />
                                            </div>
                                            <div
                                                dangerouslySetInnerHTML={{
                                                    __html:
                                                        extraPage.endpoints
                                                            .thumbnail || "",
                                                }}
                                            />
                                        </div>
                                        <div>
                                            <div
                                                className={
                                                    styles.iconPickerTextWrapper
                                                }>
                                                <span
                                                    className={
                                                        styles.iconPickerTitle
                                                    }>
                                                    {extraPage.title
                                                        ? extraPage.title
                                                        : ""}
                                                </span>
                                                <br />
                                                <span
                                                    className={
                                                        styles.iconPickerDescription
                                                    }>
                                                    {extraPage.description}
                                                </span>
                                                <div
                                                    className={
                                                        styles.buttonContainer
                                                    }>
                                                    <Button
                                                        onClick={() => {
                                                            removeExtraPageByIndex(
                                                                index,
                                                            );
                                                        }}
                                                        className={
                                                            styles.buttonRemove
                                                        }
                                                        text={
                                                            resources[
                                                                "ExtraPages_Remove"
                                                            ]
                                                        }
                                                    />
                                                    <Select
                                                        isSearchable={false}
                                                        styles={customStyles}
                                                        className={
                                                            styles.selectQuantity
                                                        }
                                                        onChange={(e: any) => {
                                                            setExtraPageQuantityByIndex(
                                                                index,
                                                                e.value,
                                                            );
                                                        }}
                                                        options={
                                                            numberOfPagesOptions
                                                        }
                                                        placeholder=""
                                                        value={selectedQuantity}
                                                        maxMenuHeight={235}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </li>
                        </React.Fragment>
                    );
                }
            })}
        </ul>
    );
};

export default SelectedExtraPages;
